import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, BookOpen, Plus, Upload, Search, CheckCircle, AlertTriangle, Clock, RefreshCw, Settings, Calendar, ArrowUp, ArrowDown, ArrowUpDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
// iter206 · Embebemos la gestión de "Reserva de Espacio" como una pestaña
// dentro de Biblioteca "Laura Iglesias". El componente standalone sigue
// existiendo y la ruta /admin/reservas se mantiene por retrocompatibilidad.
import AdminReservasPage from '../admin/AdminReservasPage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'reservas-espacio', label: 'Reservas de espacio', icon: Calendar },
  { key: 'catalogo', label: 'Catálogo', icon: BookOpen },
  { key: 'ejemplares', label: 'Ejemplares', icon: BookOpen },
  { key: 'activos', label: 'Préstamos activos', icon: Clock },
  { key: 'vencidos', label: 'Vencidos', icon: AlertTriangle },
  { key: 'importar', label: 'Importar', icon: Upload },
];

export default function AdminBibliotecaPage() {
  const [tab, setTab] = useState('reservas-espacio');
  const [config, setConfig] = useState({ dias_vencimiento: 14, dias_limite_retiro: 3 });
  const [showConfig, setShowConfig] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/biblioteca/admin/config`);
      setConfig(r.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    try {
      const r = await axios.post(`${API}/biblioteca/admin/config`, config);
      setConfig(r.data);
      setShowConfig(false);
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error('Error guardando configuración');
    }
  };

  const [wiping, setWiping] = useState(false);
  const wipeCatalogo = async () => {
    const ok = window.confirm(
      '⚠️ Vas a ELIMINAR todo el catálogo (títulos + ejemplares).\n\n' +
      'Esta acción NO se puede deshacer.\n' +
      'Se preserva el historial de préstamos para auditoría.\n\n' +
      '¿Confirmás?'
    );
    if (!ok) return;
    setWiping(true);
    try {
      const r = await axios.delete(`${API}/biblioteca/admin/catalogo/wipe`);
      toast.success(`Catálogo eliminado: ${r.data.titulos_eliminados} títulos y ${r.data.ejemplares_eliminados} ejemplares`);
      setShowConfig(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error eliminando el catálogo');
    }
    setWiping(false);
  };

  return (
    <div className="space-y-5" data-testid="admin-biblioteca-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-amber-600" /> Biblioteca "Laura Iglesias"
          </h1>
          <p className="text-sm text-gray-600">Préstamos físicos de ejemplares. Convive con el módulo Libritos.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} data-testid="btn-config-biblioteca">
          <Settings className="w-4 h-4 mr-1" /> Configuración
        </Button>
      </div>

      <div className="flex gap-1 border-b overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-2 ${
              tab === key ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === 'reservas-espacio' && <AdminReservasPage />}
      {tab === 'catalogo' && <CatalogoTab />}
      {tab === 'ejemplares' && <EjemplaresTab />}
      {tab === 'activos' && <PrestamosTab estado="activo" vacio="Sin préstamos activos" />}
      {tab === 'vencidos' && <VencidosTab />}
      {tab === 'importar' && <ImportarTab />}

      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent data-testid="dialog-config-biblioteca">
          <DialogHeader><DialogTitle>Configuración de Biblioteca</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block text-sm">
              Días de préstamo a domicilio (default)
              <Input type="number" min={1} value={config.dias_vencimiento}
                onChange={e => setConfig(c => ({ ...c, dias_vencimiento: Number(e.target.value) || 1 }))}
                data-testid="input-dias-vencimiento" />
            </label>
            <label className="block text-sm">
              Días para retirar una reserva antes de expirar
              <Input type="number" min={1} value={config.dias_limite_retiro}
                onChange={e => setConfig(c => ({ ...c, dias_limite_retiro: Number(e.target.value) || 1 }))}
                data-testid="input-dias-limite-retiro" />
            </label>

            <div className="mt-6 border-t pt-4">
              <p className="text-sm font-semibold text-red-700 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Zona peligrosa
              </p>
              <p className="text-xs text-gray-600 mb-2">
                Borra todos los títulos y ejemplares del catálogo. El historial de
                préstamos se conserva. Se bloquea si hay reservas o préstamos sin devolver.
              </p>
              <Button
                variant="outline"
                className="text-red-700 border-red-300 hover:bg-red-50"
                onClick={wipeCatalogo}
                disabled={wiping}
                data-testid="btn-wipe-catalogo"
              >
                {wiping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Eliminar todo el catálogo
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={saveConfig} data-testid="btn-save-config">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Catálogo ───
function CatalogoTab() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [nuevo, setNuevo] = useState({ numero_id: '', titulo: '', autor: '', editorial: '', tema: '' });
  const [temas, setTemas] = useState([]);
  const [temaMode, setTemaMode] = useState('existing'); // 'existing' | 'new'
  const [sortKey, setSortKey] = useState('titulo');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [showEmbed, setShowEmbed] = useState(false);
  const [manageTitulo, setManageTitulo] = useState(null); // título seleccionado para gestionar ejemplares

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/biblioteca/admin/titulos`, { params: { q, limit: 500 } });
      setItems(r.data.items || []);
    } catch (e) { toast.error('Error cargando catálogo'); }
    setLoading(false);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const loadTemas = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/biblioteca/admin/temas`);
      setTemas(r.data.temas || []);
    } catch (e) { /* silencioso */ }
  }, []);

  const openNew = () => {
    setNuevo({ numero_id: '', titulo: '', autor: '', editorial: '', tema: '' });
    setTemaMode('existing');
    loadTemas();
    setShowNew(true);
  };

  const crear = async () => {
    if (!nuevo.titulo.trim()) { toast.error('El título es obligatorio'); return; }
    try {
      await axios.post(`${API}/biblioteca/admin/titulos`, nuevo);
      toast.success('Título creado');
      setShowNew(false);
      setNuevo({ numero_id: '', titulo: '', autor: '', editorial: '', tema: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando título'); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedItems = React.useMemo(() => {
    const arr = [...items];
    const dir = sortDir === 'asc' ? 1 : -1;
    const getVal = (t) => {
      if (sortKey === 'ejemplares') return (t.ejemplares?.disponibles || 0);
      if (sortKey === 'created_at') return t.created_at || '';
      if (sortKey === 'numero_id') {
        // Si es numérico, orden numérico; si no, orden alfabético.
        const n = parseInt(t.numero_id, 10);
        return Number.isFinite(n) ? n : (t.numero_id || '').toLowerCase();
      }
      return (t[sortKey] || '').toString().toLowerCase();
    };
    arr.sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const SortHeader = ({ col, label }) => (
    <th
      className="py-2 px-2 cursor-pointer select-none hover:text-amber-700"
      onClick={() => toggleSort(col)}
      data-testid={`sort-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === col
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );

  const embedUrl = `${process.env.REACT_APP_BACKEND_URL}/api/biblioteca/publico/catalogo-embed.html`;
  const iframeSnippet = `<iframe src="${embedUrl}" style="width:100%;height:720px;border:0;" loading="lazy" title="Catálogo Biblioteca Laura Iglesias"></iframe>`;

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch (e) { toast.error('No se pudo copiar'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search className="w-4 h-4 text-gray-500 shrink-0" />
          <Input placeholder="Buscar por título, autor, editorial, tema o nº..."
            value={q} onChange={e => setQ(e.target.value)}
            className="max-w-md" data-testid="input-search-catalogo" />
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowEmbed(true)} data-testid="btn-show-embed">
            Código para Squarespace
          </Button>
          <Button size="sm" onClick={openNew} data-testid="btn-new-titulo">
            <Plus className="w-4 h-4 mr-1" /> Nuevo título
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <SortHeader col="numero_id" label="Nº" />
                  <SortHeader col="titulo" label="Título" />
                  <SortHeader col="autor" label="Autor" />
                  <SortHeader col="editorial" label="Editorial" />
                  <SortHeader col="tema" label="Tema" />
                  <SortHeader col="ejemplares" label="Ejemplares" />
                  <SortHeader col="created_at" label="Alta" />
                </tr>
              </thead>
              <tbody>
                {sortedItems.map(t => (
                  <tr key={t.id} className="border-b hover:bg-gray-50" data-testid={`row-titulo-${t.id}`}>
                    <td className="py-2 px-2 font-mono text-xs">{t.numero_id || '-'}</td>
                    <td className="py-2 px-2 font-medium">{t.titulo}</td>
                    <td className="py-2 px-2">{t.autor || '-'}</td>
                    <td className="py-2 px-2">{t.editorial || '-'}</td>
                    <td className="py-2 px-2">{t.tema || '-'}</td>
                    <td className="py-2 px-2">
                      <button
                        type="button"
                        onClick={() => setManageTitulo(t)}
                        className="flex gap-1 flex-wrap items-center hover:opacity-80 cursor-pointer group"
                        title="Gestionar ejemplares"
                        data-testid={`btn-manage-ejemplares-${t.id}`}
                      >
                        <Badge className="bg-emerald-100 text-emerald-700 group-hover:bg-emerald-200">{t.ejemplares?.disponibles || 0} disp.</Badge>
                        <Badge className="bg-blue-100 text-blue-700">{t.ejemplares?.prestados || 0} prest.</Badge>
                        {t.ejemplares?.reservados > 0 && <Badge className="bg-amber-100 text-amber-700">{t.ejemplares.reservados} res.</Badge>}
                        <span className="text-xs text-gray-400 group-hover:text-amber-700 ml-1">✎</span>
                      </button>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500 whitespace-nowrap" title={t.created_at}>
                      {t.created_at
                        ? new Date(t.created_at).toLocaleString('es-AR', {
                            day: '2-digit', month: '2-digit', year: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : '-'}
                    </td>
                  </tr>
                ))}
                {sortedItems.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-500">Sin títulos</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="dialog-new-titulo">
          <DialogHeader><DialogTitle>Nuevo título</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Nº ID (opcional)" value={nuevo.numero_id} onChange={e => setNuevo({...nuevo, numero_id: e.target.value})} data-testid="input-numero_id" />
            <Input placeholder="Título *" value={nuevo.titulo} onChange={e => setNuevo({...nuevo, titulo: e.target.value})} data-testid="input-titulo" />
            <Input placeholder="Autor" value={nuevo.autor} onChange={e => setNuevo({...nuevo, autor: e.target.value})} data-testid="input-autor" />
            <Input placeholder="Editorial" value={nuevo.editorial} onChange={e => setNuevo({...nuevo, editorial: e.target.value})} data-testid="input-editorial" />
            <div className="space-y-1">
              <label className="text-xs text-gray-600">Tema</label>
              {temaMode === 'existing' ? (
                <div className="flex gap-2">
                  <select
                    className="flex-1 border rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                    value={nuevo.tema}
                    onChange={e => {
                      if (e.target.value === '__NEW__') {
                        setTemaMode('new');
                        setNuevo({ ...nuevo, tema: '' });
                      } else {
                        setNuevo({ ...nuevo, tema: e.target.value });
                      }
                    }}
                    data-testid="select-tema"
                  >
                    <option value="">(sin tema)</option>
                    {temas.map(t => <option key={t} value={t}>{t}</option>)}
                    <option value="__NEW__">➕ Agregar nuevo tema…</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    autoFocus
                    placeholder="Nombre del nuevo tema"
                    value={nuevo.tema}
                    onChange={e => setNuevo({ ...nuevo, tema: e.target.value })}
                    data-testid="input-tema-new"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => { setTemaMode('existing'); setNuevo({ ...nuevo, tema: '' }); }}
                    data-testid="btn-tema-cancelar-new"
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={crear} data-testid="btn-crear-titulo">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEmbed} onOpenChange={setShowEmbed}>
        <DialogContent data-testid="dialog-embed">
          <DialogHeader><DialogTitle>Catálogo público para Squarespace</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-gray-600">
              Pegá este snippet dentro de una caja <strong>Code / HTML</strong> en Squarespace.
              Muestra los títulos con al menos 1 ejemplar disponible, con buscador en vivo.
            </p>
            <div>
              <label className="font-semibold text-xs text-gray-700">URL del catálogo</label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={embedUrl} data-testid="input-embed-url" />
                <Button size="sm" variant="outline" onClick={() => copyText(embedUrl, 'URL')} data-testid="btn-copy-url">Copiar</Button>
                <Button size="sm" variant="outline" onClick={() => window.open(embedUrl, '_blank')} data-testid="btn-open-embed">Abrir</Button>
              </div>
            </div>
            <div>
              <label className="font-semibold text-xs text-gray-700">Snippet &lt;iframe&gt; para Squarespace</label>
              <textarea
                readOnly
                className="w-full border rounded p-2 font-mono text-xs mt-1"
                rows={4}
                value={iframeSnippet}
                data-testid="textarea-embed-snippet"
              />
              <Button size="sm" variant="outline" className="mt-1" onClick={() => copyText(iframeSnippet, 'Snippet')} data-testid="btn-copy-snippet">
                Copiar snippet
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowEmbed(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageEjemplaresDialog
        titulo={manageTitulo}
        onClose={(didChange) => { setManageTitulo(null); if (didChange) load(); }}
      />
    </Card>
  );
}

// ─── Dialog: gestionar ejemplares de un título ───
function ManageEjemplaresDialog({ titulo, onClose }) {
  const open = !!titulo;
  const [ejemplares, setEjemplares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [creating, setCreating] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!titulo) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/biblioteca/admin/ejemplares`, { params: { titulo_id: titulo.id, limit: 500 } });
      setEjemplares(r.data || []);
    } catch (e) { toast.error('Error cargando ejemplares'); }
    setLoading(false);
  }, [titulo]);

  useEffect(() => { if (open) { setDirty(false); setCantidad(1); load(); } }, [open, load]);

  const crearBulk = async () => {
    const n = Math.max(1, Math.min(50, Number(cantidad) || 1));
    setCreating(true);
    try {
      const r = await axios.post(`${API}/biblioteca/admin/titulos/${titulo.id}/ejemplares/bulk`, null, { params: { cantidad: n } });
      toast.success(`${r.data.creados} ejemplar(es) creado(s): ${r.data.codigos.join(', ')}`);
      setDirty(true);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando ejemplares'); }
    setCreating(false);
  };

  const darBaja = async (ej) => {
    if (!window.confirm(`¿Dar de baja el ejemplar ${ej.codigo_ejemplar}? No se podrá volver a usar.`)) return;
    try {
      await axios.delete(`${API}/biblioteca/admin/ejemplares/${ej.id}`);
      toast.success('Ejemplar dado de baja');
      setDirty(true);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const estadoColor = (e) => ({
    disponible: 'bg-emerald-100 text-emerald-700',
    prestado: 'bg-blue-100 text-blue-700',
    reservado: 'bg-amber-100 text-amber-700',
    baja: 'bg-gray-200 text-gray-600',
  }[e] || 'bg-gray-100');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(dirty); }}>
      <DialogContent className="max-w-xl" data-testid="dialog-manage-ejemplares">
        <DialogHeader>
          <DialogTitle className="text-base">Ejemplares de "{titulo?.titulo}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded border border-amber-200 bg-amber-50 p-3">
            <p className="font-semibold text-amber-900 mb-2">Agregar ejemplares nuevos</p>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-700">Cantidad:</label>
              <Input type="number" min={1} max={50} value={cantidad}
                onChange={e => setCantidad(e.target.value)} className="w-24"
                data-testid="input-cantidad-ejemplares" />
              <Button size="sm" onClick={crearBulk} disabled={creating} data-testid="btn-bulk-crear-ejemplares">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Agregar
              </Button>
            </div>
            <p className="text-xs text-amber-800 mt-2">
              Los códigos se generan automáticamente a partir del Nº del título
              ({titulo?.numero_id ? <code className="bg-amber-100 px-1 rounded">{titulo.numero_id}</code> : <em>sin Nº</em>}).
              {titulo?.numero_id
                ? ' El primero mantiene el Nº y los siguientes se identifican con una letra: A, B, C…'
                : ' Al no tener Nº, se usa un prefijo EJ-XXXXXX-01, 02, …'}
            </p>
          </div>

          <div>
            <p className="font-semibold mb-2">Ejemplares actuales ({ejemplares.length})</p>
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-amber-600" /> : (
              ejemplares.length === 0 ? (
                <p className="text-gray-500 italic">Este título todavía no tiene ejemplares físicos cargados.</p>
              ) : (
                <ul className="divide-y border rounded max-h-64 overflow-y-auto">
                  {ejemplares.map(ej => (
                    <li key={ej.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`row-manage-ej-${ej.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs">{ej.codigo_ejemplar}</span>
                        <Badge className={estadoColor(ej.estado)}>{ej.estado}</Badge>
                      </div>
                      {['disponible'].includes(ej.estado) && (
                        <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => darBaja(ej)} data-testid={`btn-baja-ej-${ej.id}`}>
                          Dar de baja
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onClose(dirty)} data-testid="btn-close-manage">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Ejemplares ───
function EjemplaresTab() {
  const [tituloFilter, setTituloFilter] = useState('');
  const [titulos, setTitulos] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [nuevo, setNuevo] = useState({ titulo_id: '', codigo_ejemplar: '' });

  const loadTitulos = async () => {
    try { const r = await axios.get(`${API}/biblioteca/admin/titulos`, { params: { limit: 500 } }); setTitulos(r.data.items || []); }
    catch (e) { /* no-op */ }
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tituloFilter) params.titulo_id = tituloFilter;
      const r = await axios.get(`${API}/biblioteca/admin/ejemplares`, { params });
      setItems(r.data || []);
    } catch (e) { toast.error('Error cargando ejemplares'); }
    setLoading(false);
  }, [tituloFilter]);

  useEffect(() => { loadTitulos(); }, []);
  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!nuevo.titulo_id || !nuevo.codigo_ejemplar.trim()) { toast.error('Elegí título y codigo'); return; }
    try {
      await axios.post(`${API}/biblioteca/admin/ejemplares`, nuevo);
      toast.success('Ejemplar creado');
      setShowNew(false);
      setNuevo({ titulo_id: '', codigo_ejemplar: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const tituloMap = Object.fromEntries(titulos.map(t => [t.id, t]));
  const estadoColor = (e) => ({ disponible: 'bg-emerald-100 text-emerald-700', prestado: 'bg-blue-100 text-blue-700', reservado: 'bg-amber-100 text-amber-700', baja: 'bg-gray-100 text-gray-500' }[e] || 'bg-gray-100');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <select value={tituloFilter} onChange={e => setTituloFilter(e.target.value)} className="border rounded px-2 py-1 text-sm max-w-xs" data-testid="select-filter-titulo">
          <option value="">Todos los títulos</option>
          {titulos.map(t => <option key={t.id} value={t.id}>{t.titulo}</option>)}
        </select>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="btn-new-ejemplar">
          <Plus className="w-4 h-4 mr-1" /> Nuevo ejemplar
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6 text-amber-600" /> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-600">
              <th className="py-2 px-2">Código</th>
              <th className="py-2 px-2">Título</th>
              <th className="py-2 px-2">Estado</th>
            </tr></thead>
            <tbody>
              {items.map(e => (
                <tr key={e.id} className="border-b hover:bg-gray-50" data-testid={`row-ejemplar-${e.id}`}>
                  <td className="py-2 px-2 font-mono text-xs">{e.codigo_ejemplar}</td>
                  <td className="py-2 px-2">{tituloMap[e.titulo_id]?.titulo || '-'}</td>
                  <td className="py-2 px-2"><Badge className={estadoColor(e.estado)}>{e.estado}</Badge></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-gray-500">Sin ejemplares</td></tr>}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="dialog-new-ejemplar">
          <DialogHeader><DialogTitle>Nuevo ejemplar</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <select value={nuevo.titulo_id} onChange={e => setNuevo({...nuevo, titulo_id: e.target.value})} className="w-full border rounded px-2 py-2" data-testid="select-titulo-ejemplar">
              <option value="">Elegí un título...</option>
              {titulos.map(t => <option key={t.id} value={t.id}>{t.titulo}</option>)}
            </select>
            <Input placeholder="Código del ejemplar (ej: EJ-001)" value={nuevo.codigo_ejemplar} onChange={e => setNuevo({...nuevo, codigo_ejemplar: e.target.value})} data-testid="input-codigo-ejemplar" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={crear} data-testid="btn-crear-ejemplar">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Préstamos (activos y reservados) ───
function PrestamosTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos'); // todos | reservado | activo
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro !== 'todos') params.estado = filtro;
      const r = await axios.get(`${API}/biblioteca/admin/prestamos`, { params });
      // Filtrar a reservados + activos si "todos"
      const list = (r.data.items || []).filter(x => ['reservado', 'activo'].includes(x.estado));
      setItems(list);
    } catch (e) { toast.error('Error cargando préstamos'); }
    setLoading(false);
  }, [filtro]);
  useEffect(() => { load(); }, [load]);

  const [retirarDialog, setRetirarDialog] = useState({ open: false, prestamo: null, tipo: 'domicilio' });

  const retirar = async () => {
    try {
      await axios.post(`${API}/biblioteca/admin/prestamos/${retirarDialog.prestamo.id}/retirar`, { tipo: retirarDialog.tipo });
      toast.success('Retiro registrado');
      setRetirarDialog({ open: false, prestamo: null, tipo: 'domicilio' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const devolver = async (p) => {
    if (!window.confirm(`¿Registrar devolución del ejemplar ${p.ejemplar?.codigo_ejemplar}?`)) return;
    try {
      await axios.post(`${API}/biblioteca/admin/prestamos/${p.id}/devolver`);
      toast.success('Devolución registrada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1">
          {['todos', 'reservado', 'activo'].map(f => (
            <button key={f} onClick={() => setFiltro(f)}
              className={`px-3 py-1 text-xs rounded-full border ${filtro === f ? 'bg-amber-100 border-amber-400 text-amber-800' : 'bg-white'}`}
              data-testid={`filtro-${f}`}>
              {f}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6 text-amber-600" /> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-600">
              <th className="py-2 px-2">Estado</th>
              <th className="py-2 px-2">Matrícula</th>
              <th className="py-2 px-2">Título</th>
              <th className="py-2 px-2">Ejemplar</th>
              <th className="py-2 px-2">Fecha</th>
              <th className="py-2 px-2">Vence</th>
              <th className="py-2 px-2 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`row-prestamo-${p.id}`}>
                  <td className="py-2 px-2"><Badge className={p.estado === 'reservado' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>{p.estado}</Badge></td>
                  <td className="py-2 px-2 font-mono">{p.numero_matricula}</td>
                  <td className="py-2 px-2">{p.titulo?.titulo || '-'}</td>
                  <td className="py-2 px-2 font-mono text-xs">{p.ejemplar?.codigo_ejemplar || '-'}</td>
                  <td className="py-2 px-2 text-xs text-gray-500">{p.fecha_prestamo ? new Date(p.fecha_prestamo).toLocaleDateString('es-AR') : (p.fecha_reserva ? `(res: ${new Date(p.fecha_reserva).toLocaleDateString('es-AR')})` : '-')}</td>
                  <td className="py-2 px-2 text-xs">{p.fecha_vencimiento ? new Date(p.fecha_vencimiento).toLocaleDateString('es-AR') : '-'}</td>
                  <td className="py-2 px-2 text-right space-x-1">
                    {p.estado === 'reservado' && (
                      <Button size="sm" variant="outline" onClick={() => setRetirarDialog({ open: true, prestamo: p, tipo: 'domicilio' })} data-testid={`btn-retirar-${p.id}`}>Retirar</Button>
                    )}
                    {['activo', 'reservado'].includes(p.estado) && (
                      <Button size="sm" variant="outline" onClick={() => devolver(p)} className="text-emerald-700" data-testid={`btn-devolver-${p.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Devolver
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-500">Sin préstamos</td></tr>}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={retirarDialog.open} onOpenChange={o => setRetirarDialog(d => ({...d, open: o}))}>
        <DialogContent data-testid="dialog-retirar">
          <DialogHeader><DialogTitle>Registrar retiro</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p><strong>Título:</strong> {retirarDialog.prestamo?.titulo?.titulo}</p>
            <p><strong>Ejemplar:</strong> {retirarDialog.prestamo?.ejemplar?.codigo_ejemplar}</p>
            <p><strong>Matrícula:</strong> {retirarDialog.prestamo?.numero_matricula}</p>
            <label className="block mt-3">
              Tipo:
              <select value={retirarDialog.tipo} onChange={e => setRetirarDialog(d => ({...d, tipo: e.target.value}))} className="w-full border rounded px-2 py-2" data-testid="select-tipo-retiro">
                <option value="domicilio">Domicilio (con fecha de vencimiento)</option>
                <option value="sala">Sala (sin vencimiento)</option>
              </select>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetirarDialog({ open: false, prestamo: null, tipo: 'domicilio' })}>Cancelar</Button>
            <Button onClick={retirar} data-testid="btn-confirm-retirar">Confirmar retiro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Vencidos ───
function VencidosTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/biblioteca/admin/vencidos`); setItems(r.data || []); }
    catch (e) { toast.error('Error cargando vencidos'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Préstamos vencidos</CardTitle>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6 text-amber-600" /> : (
          items.length === 0 ? <p className="py-8 text-center text-gray-500">🎉 Sin préstamos vencidos</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-600">
                <th className="py-2 px-2">Matrícula</th>
                <th className="py-2 px-2">Título</th>
                <th className="py-2 px-2">Vencido desde</th>
                <th className="py-2 px-2">Días</th>
              </tr></thead>
              <tbody>
                {items.map(p => {
                  const diasVencido = Math.floor((Date.now() - new Date(p.fecha_vencimiento).getTime()) / 86400000);
                  return (
                    <tr key={p.id} className="border-b hover:bg-red-50" data-testid={`row-vencido-${p.id}`}>
                      <td className="py-2 px-2 font-mono">{p.numero_matricula}</td>
                      <td className="py-2 px-2">{p.titulo?.titulo || '-'}</td>
                      <td className="py-2 px-2">{new Date(p.fecha_vencimiento).toLocaleDateString('es-AR')}</td>
                      <td className="py-2 px-2"><Badge className="bg-red-100 text-red-700">{diasVencido}d</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </CardContent>
    </Card>
  );
}

// ─── Importar ───
function ImportarTab() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const subir = async () => {
    if (!file) { toast.error('Seleccioná un archivo CSV o Excel'); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/biblioteca/admin/importar`, fd);
      setResult(r.data);
      toast.success(
        `Import OK: ${r.data.titulos_creados ?? r.data.creados} título(s)` +
        (r.data.ejemplares_creados ? ` · ${r.data.ejemplares_creados} ejemplar(es)` : '')
      );
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setUploading(false);
  };

  const exportar = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/biblioteca/admin/catalogo/exportar.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const now = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '').slice(0, 13);
      a.download = `biblioteca-catalogo-${now}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch (e) { toast.error('Error exportando catálogo'); }
    setExporting(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" /> Importar / Exportar catálogo</CardTitle>
        <Button size="sm" variant="outline" onClick={exportar} disabled={exporting} data-testid="btn-exportar-csv">
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Exportar CSV
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          <p className="font-semibold mb-1">Formato esperado (header obligatorio):</p>
          <p className="font-mono">numero_id, titulo, autor, editorial, tema</p>
          <p className="mt-1">Solo <code>titulo</code> es obligatorio. Admite .csv y .xlsx.</p>
          <p className="mt-2 border-t border-blue-200 pt-2">
            <strong>Agrupación automática:</strong> si varias filas comparten el prefijo numérico de
            <code className="mx-1">numero_id</code>
            (ej. <code>835</code>, <code>835A</code>, <code>835B</code>), se crea
            <strong> UN sólo título</strong> y las demás filas se agregan como
            <strong> ejemplares físicos adicionales</strong>. Si el título ya existe en el catálogo,
            las filas nuevas sólo suman ejemplares.
          </p>
        </div>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="input-file-importar" />
        <div>
          <Button onClick={subir} disabled={!file || uploading} data-testid="btn-importar">
            {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importando...</> : 'Importar'}
          </Button>
        </div>
        {result && (
          <div className="rounded border p-3 text-sm bg-emerald-50 border-emerald-200">
            <p><strong>Títulos creados:</strong> {result.titulos_creados ?? result.creados}</p>
            {result.ejemplares_creados !== undefined && (
              <p><strong>Ejemplares creados:</strong> {result.ejemplares_creados}</p>
            )}
            <p><strong>Errores:</strong> {result.errores_count}</p>
            {result.errores_count > 0 && (
              <details className="mt-2"><summary className="cursor-pointer text-xs text-red-600">Ver detalles</summary>
                <ul className="text-xs mt-1">
                  {result.errores.map((e, i) => <li key={i}>Fila {e.fila}: {e.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
